<script setup>
import { onMounted, ref, watch } from "vue";
import ArgonTable from "@/components/table/ArgonTable.vue";
import ArgonTd from "@/components/table/ArgonTd.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import CopyValue from "@/components/CopyValue.vue";
import ArgonStatus from "@/components/ArgonStatus.vue";
import ArgonBadge from "@/components/ArgonBadge.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import ActionsDropdown from "@/components/dropdown/ActionsDropdown.vue";
import UserTypeBadge from "@/components/badges/UserType.vue";
import ConfirmModal from "@/components/modal/ConfirmModal.vue";
import PlansCalendar from "./PlansCalendar.vue";
import ArgonViewSwitcher from "@/components/ArgonViewSwitcher.vue";
import { useRouter } from "vue-router";
import { argonTableStore } from "@/_store/argonTableStore";
import { planStore } from "@/_store/planStore";
import { accountStore } from "@/_store/accountStore";
import { userStore } from "@/_store/userStore";
import { errorUtils, fileUtils } from "@/utils";
import { PLAN_TYPE_OPTIONS } from "@/utils/formatters";
import moment from "moment";
import { defaultFilterDates } from "@/maestros/defaultDatesValues";
import ModalPlan from "./ModalPlan.vue";
import { getAccountPlansView } from "@/views/maps/map-style-utils";
import ESTATUS_ENUM from "@/Constans/EstatusEnum";

const tableStore = argonTableStore();
const store = planStore();
const accountsStore = accountStore();
const userStores = userStore();
const router = useRouter();
const snackbar = ref(null);
const showModal = ref(false);
const showFinalizeConfirmModal = ref(false);
const showSendToRouteConfirmModal = ref(false);
const pendingPlanIds = ref([]);
const viewMode = ref(getAccountPlansView(accountsStore.account));
const calendarMonth = ref(moment());
let hasCustomViewModeSelection = false;
const viewOptions = [
  { value: "table", icon: "fa fa-list", title: "Vista tabla" },
  { value: "calendar", icon: "fa fa-calendar", title: "Vista calendario" },
];
const columns = ref([
  { header: "Fecha", field: "date", sort: false, position: "start" },
  { header: "Nombre", field: "name", sort: false, position: "start" },
  { header: "Tipo", field: "type", sort: false, position: "center" },
  {
    header: "Paradas pendientes",
    field: "route_stops_pending",
    sort: false,
    position: "center",
  },
  {
    header: "Paradas finalizadas",
    field: "route_stops_finalized",
    sort: false,
    position: "center",
  },
  {
    header: "Paradas con incidencia",
    field: "route_stops_incidence",
    sort: false,
    position: "center",
  },
  {
    header: "Estado",
    field: "status",
    sort: false,
    position: "center",
    width: "w-5",
  },
  { header: "", field: "actions", sort: false, position: "center", width: "w-1" },
]);
const customFilterDate = ref(store.customFilterDate);
const statusOptions = Object.entries(ESTATUS_ENUM.plan).map(
  ([value, meta]) => ({
    label: meta?.label || value,
    value,
  })
);
const fieldFilters = ref([
  {
    field: "name",
    description: "Nombre",
  },
]);
const defaultFilters = ref([
  {
    field: "status",
    label: "Estado",
    multi: true,
    options: statusOptions,
  },
  {
    field: "type",
    label: "Tipo",
    multi: false,
    options: PLAN_TYPE_OPTIONS,
  },
  {
    field: "date",
    label: "Fecha",
    multi: false,
    isDate: true,
    options: [
      ...defaultFilterDates,
      {
        label: "Personalizado",
        value: customFilterDate,
        isCustomDate: true,
        storage: store,
        formatDate: "DD/MM/YYYY",
      },
    ],
  },
]);

onMounted(() => {
  tableStore.clear();
  const savedFilters = userStores.filtersPage.plans?.filters || [];
  tableStore.changeFilters(savedFilters);
  viewMode.value = getAccountPlansView(accountsStore.account);
  if (!accountsStore.account) {
    accountsStore.getAccount().catch(() => null);
  }
  if (viewMode.value === "calendar") {
    reloadCalendar();
  } else {
    reloadPage();
  }
});

function reloadPage() {
  userStores.filtersPage.plans = {
    filters: tableStore.filters,
    filtersFavorite: tableStore.filtersFavorite,
  };
  store.getPlans(tableStore.pagination, tableStore.query, tableStore.sort);
}

function getCalendarGridRange(month) {
  const from = month.clone().startOf("month").startOf("isoWeek");
  const to = month.clone().endOf("month").endOf("isoWeek");
  return { from, to };
}

function reloadCalendar() {
  const { from, to } = getCalendarGridRange(calendarMonth.value);
  store.getPlansByDateRange(from.format("YYYY-MM-DD"), to.format("YYYY-MM-DD"));
}

async function exportPlans() {
  const bytes = await store.exportar(
    tableStore.pagination,
    tableStore.query,
    tableStore.sort
  );

  fileUtils.downloadBytesFile(
    bytes,
    fileUtils.generateDownloadFileName("xlsx")
  );
}

function onSortChanged() {
  tableStore.clearPagination();
  reloadPage();
}

function openNewPlanModal() {
  showModal.value = true;
}

function prevMonth() {
  calendarMonth.value = calendarMonth.value.clone().subtract(1, "month");
  reloadCalendar();
}

function nextMonth() {
  calendarMonth.value = calendarMonth.value.clone().add(1, "month");
  reloadCalendar();
}


function onCalendarClickPlan(plan) {
  onClick(plan);
}

function closeModal() {
  showModal.value = false;
}

function successCreatePlan(plan) {
  showModal.value = false;
  router.push({ path: `/plan/${plan._id}`, state: { plan_name: plan.name } });
}
function onClick(val) {
  const planId = val?._id;
  const planName = typeof val?.name === "string" ? val.name.trim() : "";
  router.push({
    path: `/plan/${planId}`,
    state: planName ? { plan_name: planName } : undefined,
  });
}
function getPosition(field) {
  const column = columns.value.find((col) => col.field === field);
  return column?.position || "start";
}

function toCount(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return parsed;
}

function getTypeLabel(type) {
  const normalized = String(type || "").trim().toUpperCase();
  if (!normalized) return "";
  if (normalized === "RECOGIDA") return "RECOGIDA";
  if (normalized === "ENTREGA") return "ENTREGA";
  if (normalized === "AMBOS") return "AMBOS";
  return normalized;
}

const closeSnackbar = () => {
  snackbar.value = null;
};

function openSendToRouteConfirmModal(planIds = tableStore.checkedList) {
  const normalizedPlanIds = Array.isArray(planIds) ? planIds.filter(Boolean) : [];
  if (!normalizedPlanIds.length) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos un plan.",
      type: "danger",
      color: "white",
    };
    return;
  }

  pendingPlanIds.value = [...new Set(normalizedPlanIds)];
  showSendToRouteConfirmModal.value = true;
}

function openFinalizeConfirmModal(planIds = tableStore.checkedList) {
  const normalizedPlanIds = Array.isArray(planIds) ? planIds.filter(Boolean) : [];
  if (!normalizedPlanIds.length) {
    snackbar.value = {
      title: "Error",
      message: "Debe seleccionar al menos un plan.",
      type: "danger",
      color: "white",
    };
    return;
  }

  pendingPlanIds.value = [...new Set(normalizedPlanIds)];
  showFinalizeConfirmModal.value = true;
}

function confirmSendToRoutePlans() {
  const planIds = pendingPlanIds.value.length
    ? pendingPlanIds.value
    : tableStore.checkedList;
  store.sendPlanStatusMulti(planIds);
  pendingPlanIds.value = [];
  showSendToRouteConfirmModal.value = false;
}

function confirmFinalizePlans() {
  const planIds = pendingPlanIds.value.length
    ? pendingPlanIds.value
    : tableStore.checkedList;
  store.deletePlanStatusMulti(planIds);
  pendingPlanIds.value = [];
  showFinalizeConfirmModal.value = false;
}

function getRowActionsMenuId(planItem) {
  return `plan-row-actions-${String(planItem?._id || "unknown").replace(
    /[^a-zA-Z0-9_-]/g,
    "_"
  )}`;
}

function getRowActions(planItem) {
  const status = String(planItem?.status || "").toUpperCase();
  const canSendToRoute = status === "PENDIENTE";
  const canFinalize = status !== "FINALIZADO";

  return [
    [{ label: "Abrir", event: "open" }],
    [
      {
        label: "En Ruta",
        event: "sendToRoute",
        disabled: !canSendToRoute,
        tooltip: canSendToRoute ? "" : "Solo disponible para planes en estado borrador.",
      },
      {
        label: "Finalizar",
        event: "finalize",
        disabled: !canFinalize,
        tooltip: canFinalize ? "" : "El plan ya está finalizado.",
      },
    ],
  ];
}

function onRowAction(planItem, action) {
  const actionEvent = action?.event;
  if (!actionEvent || !planItem?._id) return;

  if (actionEvent === "open") {
    onClick(planItem);
    return;
  }

  if (actionEvent === "sendToRoute") {
    openSendToRouteConfirmModal([planItem._id]);
    return;
  }

  if (actionEvent === "finalize") {
    openFinalizeConfirmModal([planItem._id]);
  }
}

watch(
  () => accountsStore.account,
  (value) => {
    if (!value || hasCustomViewModeSelection) return;
    viewMode.value = getAccountPlansView(value);
  }
);
watch(viewMode, (value) => {
  hasCustomViewModeSelection = true;
  if (value === "calendar") {
    tableStore.clearSelection();
  }
  reloadCurrentView();
});
function reloadCurrentView() {
  if (viewMode.value === "calendar") {
    reloadCalendar();
    return;
  }
  reloadPage();
}

watch(
  () => store.planUpdated,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "OK",
        message: "Solicitud enviada con exito",
        type: "info",
        color: "white",
      };
      reloadCurrentView();
    }
  }
);
watch(
  () => store.planDeleted,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "OK",
        message: "Solicitud enviada con exito",
        type: "info",
        color: "white",
      };
      reloadCurrentView();
    }
  }
);
watch(
  () => store.error,
  (value) => {
    if (!value) return;
    if (showModal.value) return;
    snackbar.value = {
      title: "Error",
      message: errorUtils.getErrorMessageHtml(value),
      type: "danger",
      color: "white",
    };
  }
);
</script>

<template>
  <div class="card plans-table-view" :class="{ 'is-calendar-mode': viewMode === 'calendar' }">
    <div class="card-body plans-table-body">
      <argon-table
        :columns="columns"
        :items="store.plans || []"
        :default-filters="defaultFilters"
        :field-filters="fieldFilters"
        :show-importar="false"
        :show-filters="viewMode !== 'calendar'"
        :show-search="viewMode !== 'calendar'"
        :show-pagination="viewMode !== 'calendar'"
        @filter-changed="reloadPage"
        @on-reload="reloadPage"
        @sort-changed="onSortChanged"
        @page-changed="reloadPage"
        @new-item="openNewPlanModal"
        @on-click="onClick"
        @export-data="exportPlans"
      >
        <template #buttons>
          <div
            class="dropdown d-inline"
            data-dropdown-static
            style="--dropdown-gap: 4px"
          >
            <argon-button
              id="navbarDropdownPrinter"
              color="primary"
              variant="outline"
              class="dropdown-toggle me-2 btn-xs"
              data-bs-toggle="dropdown"
              data-bs-display="static"
              aria-expanded="false"
            >
              <i class="fa fa-exchange me-sm-2"></i>Acciones
            </argon-button>
            <ul
              class="dropdown-menu z-index-1030 dropdown-menu-lg-start px-2 py-3"
              aria-labelledby="navbarDropdownPrinter"
              style
            >
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  @click="() => openSendToRouteConfirmModal()"
                >
                  En Ruta
                </a>
              </li>
              <li>
                <a
                  class="dropdown-item border-radius-md"
                  @click="() => openFinalizeConfirmModal()"
                >
                  Finalizar
                </a>
              </li>
            </ul>
          </div>
        </template>
        <template #button-custom>
          <argon-view-switcher
            v-model="viewMode"
            :options="viewOptions"
            class="me-2"
          />
        </template>
        <template #default="{ item }">
          <argon-td
            :position="getPosition('date')"
            :text-primary="moment(item.date).format('DD/MM/YYYY')"
          />
          <argon-td
            :position="getPosition('name')"
            :text-primary="item.name"
          >
            <template v-if="item.name" #append>
              <copy-value :value-to-copy="item.name" size="xs" show-on-hover />
            </template>
          </argon-td>
          <argon-td :position="getPosition('type')">
            <user-type-badge
              style="min-width: 100px"
              class="badge badge-md"
              :item="getTypeLabel(item.type)"
            />
          </argon-td>
          <argon-td :position="getPosition('route_stops_pending')">
            <argon-badge size="sm" color="secondary">{{ toCount(item.route_stops_pending) }}</argon-badge>
          </argon-td>
          <argon-td :position="getPosition('route_stops_finalized')">
            <argon-badge size="sm" color="secondary">{{ toCount(item.route_stops_finalized) }}</argon-badge>
          </argon-td>
          <argon-td :position="getPosition('route_stops_incidence')">
            <argon-badge size="sm" color="secondary">{{ toCount(item.route_stops_incidence) }}</argon-badge>
          </argon-td>
          <argon-td :position="getPosition('status')">
            <argon-status entity="plan" :status="item.status" width="100px" />
          </argon-td>
          <argon-td :position="getPosition('actions')">
            <div class="table-row-actions">
              <actions-dropdown
                :menu-id="getRowActionsMenuId(item)"
                :actions="getRowActions(item)"
                aria-label="Acciones del plan"
                trigger-class="table-action-button"
                menu-class="table-action-menu-dropdown"
                @action="(action) => onRowAction(item, action)"
              />
            </div>
          </argon-td>
        </template>
      </argon-table>
      <div v-show="viewMode === 'calendar'" class="plans-calendar-wrap">
        <plans-calendar
          :plans="store.plansCalendar"
          :month="calendarMonth"
          @click-plan="onCalendarClickPlan"
          @prev-month="prevMonth"
          @next-month="nextMonth"

        />
      </div>
    </div>
  </div>

  <modal-plan
    v-if="showModal"
    @close="closeModal"
    @success="successCreatePlan"
  />
  <confirm-modal
    v-if="showSendToRouteConfirmModal"
    v-model="showSendToRouteConfirmModal"
    title="Estas seguro de querer poner en ruta los planes?"
    text="No podrá revertir esta acción!"
    @confirm="confirmSendToRoutePlans"
  />
  <confirm-modal
    v-if="showFinalizeConfirmModal"
    v-model="showFinalizeConfirmModal"
    title="Estas seguro de querer finalizar los planes?"
    text="No podrá revertir esta acción!"
    @confirm="confirmFinalizePlans"
  />

  <div class="position-fixed top-0 end-0 z-index-2 p-3">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped lang="scss">
.is-calendar-mode :deep(#argon-table) {
  display: none !important;
}

.is-calendar-mode :deep(#argon-table-container) {
  height: auto;
  max-height: none;
  overflow: visible;
  flex: 0 0 auto;
}

.plans-calendar-wrap {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 8px 0 0;
}


:deep(thead th.text-center.w-1:last-child),
:deep(tbody td.text-center:last-child) {
  max-width: 48px;
  min-width: 48px;
  width: 48px;
}
</style>
